// 마을 상점·대장간 창 본문 배치(순수 계산, 2026-10-27 「글뿐인 목록 → 아이콘 카드」).
// 창은 설계 좌표(CSS zoom) 안에서 그려지므로 입력은 본문 안쪽 폭(설계 px) 하나다. DOM·three import 금지.
// 가로 화면 본문 ≈ 560, 세로 화면(폰) 본문 ≈ 340~520. 폭이 좁으면 카드의 가격·구매 줄을 아래로 내려 구매 단추가 잘리지 않게 한다.

/** 본문 폭을 모를 때(첫 그림, 측정값 0) 쓰는 가로 화면 값 */
export const DEFAULT_BODY_W = 560;
/** 가격·구매 열 폭(설계 px) */
export const BUY_COL_W = 104;
/** 이 폭보다 좁으면 카드 글줄이 너무 좁아져 가격·구매를 아래 줄로 내린다 */
const TEXT_MIN_W = 190;

/** 상품 카드 한 줄 배치 */
export interface RowLayout {
  /** 아이콘 칸 한 변(px) */
  readonly icon: number;
  /** true면 가격·구매가 카드 아래 줄(가격 왼쪽, 단추 오른쪽), false면 오른쪽 열 */
  readonly stack: boolean;
  /** grid-template-columns */
  readonly columns: string;
}

/** 본문 폭 → 카드 아이콘 크기·가격 위치. 폭 0 이하·NaN은 기본(가로 화면) */
export function shopRowLayout(bodyW: number, buyW = BUY_COL_W): RowLayout {
  const w = Number.isFinite(bodyW) && bodyW > 0 ? bodyW : DEFAULT_BODY_W;
  const icon = w >= 460 ? 72 : w >= 360 ? 64 : 56;
  const gap = 10;
  const stack = w - icon - buyW - gap * 2 < TEXT_MIN_W;
  return { icon, stack, columns: stack ? `${icon}px minmax(0,1fr)` : `${icon}px minmax(0,1fr) ${buyW}px` };
}

/** 정사각 칸 격자: 칸 수와 한 변(px) */
export interface TileGrid {
  readonly cols: number;
  readonly tile: number;
  readonly gap: number;
}
/**
 * 폭 안에 min 이상인 정사각 칸을 최대한 채운다(칸 한 변은 정수). 칸은 항상 정사각이라 그림이 잘리지 않는다.
 * maxCols는 한 줄 칸 수 상한(칸이 너무 작아지는 것을 막는 min과 별개로, 너무 커지는 것도 max로 막는다)
 */
export function tileGrid(width: number, min = 56, gap = 6, maxCols = 8, maxTile = 96): TileGrid {
  const w = Number.isFinite(width) && width > 0 ? width : DEFAULT_BODY_W;
  let cols = Math.max(1, Math.min(maxCols, Math.floor((w + gap) / (min + gap))));
  let tile = Math.floor((w - gap * (cols - 1)) / cols);
  // 칸이 상한보다 커지면 칸 수를 늘려 줄인다(칸이 min 밑으로 내려가면 멈춘다)
  while (tile > maxTile && cols < maxCols) {
    const next = Math.floor((w - gap * cols) / (cols + 1));
    if (next < min) break;
    cols++; tile = next;
  }
  return { cols, tile: Math.min(tile, maxTile), gap };
}

/** 세로 스크롤이 생길 때 줄이 차지하는 폭(scrollbar-width:thin 기준 넉넉히) */
export const SCROLLBAR_W = 12;
export interface ScrollGrid extends TileGrid {
  /** 세로 스크롤이 생기는가(줄 수 > visibleRows) */
  readonly scroll: boolean;
  /** 목록 높이 상한(보이는 줄만큼 + 안쪽 여백) */
  readonly maxHeight: number;
}
/**
 * 스크롤이 생기는 정사각 격자. 장비가 많아 줄이 visibleRows를 넘으면 스크롤바 폭만큼 빼고 칸을 다시 정해, 마지막 열이 잘리거나
 * 가로 스크롤이 생기지 않게 한다. width는 목록 안쪽(안쪽 여백 뺀) 폭
 */
export function scrollTileGrid(count: number, width: number, visibleRows = 4, min = 56, gap = 6, maxCols = 8, maxTile = 96): ScrollGrid {
  const n = Math.max(0, Math.floor(count));
  let g = tileGrid(width, min, gap, maxCols, maxTile);
  let scroll = Math.ceil(n / g.cols) > visibleRows;
  if (scroll) {
    g = tileGrid(width - SCROLLBAR_W, min, gap, maxCols, maxTile);
    scroll = Math.ceil(n / g.cols) > visibleRows;
  }
  return { ...g, scroll, maxHeight: g.tile * visibleRows + g.gap * (visibleRows - 1) + 4 };
}

/** 대장간 강화·보석 탭의 왼쪽(장비 격자) / 오른쪽(상세) 폭 */
export interface SplitLayout {
  readonly stacked: boolean;
  readonly leftW: number;
  readonly rightW: number;
  readonly gap: number;
}
/** narrow(세로 화면)이면 위아래로 쌓는다. 가로는 왼쪽 ≈ 42%(200~270), 나머지가 상세 */
export function forgeSplit(bodyW: number, narrow: boolean): SplitLayout {
  const w = Number.isFinite(bodyW) && bodyW > 0 ? bodyW : DEFAULT_BODY_W;
  const gap = 12;
  if (narrow) return { stacked: true, leftW: w, rightW: w, gap };
  const leftW = Math.max(200, Math.min(270, Math.round((w - gap) * 0.42)));
  return { stacked: false, leftW, rightW: Math.max(0, w - gap - leftW), gap };
}

/** 보석 소켓 칸 한 변(px): 넓으면 44, 좁으면 38 */
export function socketSize(bodyW: number): number {
  return bodyW >= 460 || !(bodyW > 0) ? 44 : 38;
}
